import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../components/layout/PageContainer";
import { Card } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { Button } from "../components/ui/Button";
import { CustomerInfoCard } from "../components/features/CustomerInfoCard";
import { ManageAccessModal } from "../components/features/ManageAccessModal";
import {
  CustomerEditDialog,
  CustomerCreateDialog,
} from "../components/features/customer";
import { DisclaimerModal } from "../components/features/DisclaimerModal";
import { ROUTES } from "../constants/routes";
import { useCustomer } from "../contexts/CustomerContext";
import { useAuth } from "../contexts/AuthContext";
import { customerApi } from "../services/customerService";
import {
  BarChart3,
  FileText,
  Search,
  TrendingUp,
  Sparkles,
  Settings,
  Trash2,
  Edit,
  Users,
  AlertCircle,
} from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    selectedCustomer,
    selectCustomer,
    clearSelectedCustomer,
    loadAccessibleCustomers,
    accessibleCustomers,
    addAccessibleCustomer,
  } = useCustomer();
  const { user } = useAuth();

  const [manageAccessOpen, setManageAccessOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  // Check if user can manage access (admin or consultant)
  const canManageAccess = user?.role === "admin" || user?.role === "consultant";
  const isAdmin = user?.role === "admin";
  const isRegularUser = user?.role === "user";

  // Check if user can create customers
  const canCreateCustomer = isRegularUser
    ? accessibleCustomers?.length === 0 // Regular users: only if no customers
    : true; // Admin/Consultant: always

  /**
   * Handle customer updated
   */
  const handleCustomerUpdated = async (updatedCustomer) => {
    // Update the selected customer with the new data
    selectCustomer(updatedCustomer);
    // Reload accessible customers list
    await loadAccessibleCustomers();
    // Trigger refresh of customer info card
    setRefreshKey((prev) => prev + 1);
  };

  /**
   * Handle customer created successfully
   */
  const handleCustomerCreated = (newCustomer) => {
    // Add the new customer to the accessible customers list (instant UI update)
    addAccessibleCustomer(newCustomer);

    // Auto-select the newly created customer
    selectCustomer(newCustomer);
  };

  /**
   * Handle delete customer
   */
  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;

    try {
      setIsDeleting(true);

      // Call delete API
      await customerApi.deleteCustomer(selectedCustomer.id);

      // Clear selection and reload customers
      clearSelectedCustomer();
      await loadAccessibleCustomers();

      // Close dialog
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting customer:", error);
      alert(
        error.response?.data?.message ||
          "Failed to delete customer. Please try again.",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <PageContainer
        title="Dashboard"
        subtitle={`Overview for ${selectedCustomer?.name || "All Customers"}`}
        breadcrumbs={[{ label: "Home", href: ROUTES.DASHBOARD }]}
        actions={
          selectedCustomer && (
            <div className="flex items-center gap-2">
              {/* Edit Customer Button (Admin/Consultant) */}
              {canManageAccess && (
                <button
                  onClick={() => setEditDialogOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
                >
                  <Edit className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium text-sm leading-none">
                    Edit Customer
                  </span>
                </button>
              )}

              {/* Manage Access Button (Admin/Consultant) */}
              {canManageAccess && (
                <button
                  onClick={() => setManageAccessOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium text-sm leading-none">
                    Manage Access
                  </span>
                </button>
              )}

              {/* Delete Customer Button (Admin Only) */}
              {isAdmin && (
                <button
                  onClick={() => setDeleteDialogOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-300 rounded-md bg-white hover:bg-red-50 transition-colors text-red-600"
                >
                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium text-sm leading-none">
                    Delete Customer
                  </span>
                </button>
              )}
            </div>
          )
        }
      >
        {/* Customer Info Card */}
        {selectedCustomer ? (
          <div className="flex flex-col gap-8">
            <CustomerInfoCard customer={selectedCustomer} key={refreshKey} />

            {/* Welcome Message */}
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Welcome to conRIO
              </h2>
              <p className="text-gray-700 text-sm">
                Manage and analyze SAP Digital Manufacturing services for{" "}
                {selectedCustomer.name}. Use the sidebar navigation to access
                different features and tools.
              </p>
            </Card>

            {/* Disclaimer Banner */}
            <Card>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-amber-900 mb-1">
                    Important Disclaimer
                  </p>
                  <p className="text-amber-800">
                    This application provides analysis based on user-configured
                    data. Results depend on accurate service configuration.{" "}
                    <button
                      onClick={() => setShowDisclaimerModal(true)}
                      className="underline font-medium hover:text-amber-900"
                    >
                      Read full disclaimer
                    </button>
                  </p>
                </div>
              </div>
            </Card>

            {/* Two Column Grid for Guide & Features */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Getting Started Guide */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Quick Guide
                </h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <Step
                    number="1"
                    text="Browse Service Catalog to discover available SAP services"
                  />
                  <Step
                    number="2"
                    text="Assign services to your customer in Service Usage"
                  />
                  <Step
                    number="3"
                    text="Analyze Change Impact to track SAP updates and deprecations"
                  />
                  <Step
                    number="4"
                    text="Generate comprehensive reports for documentation"
                  />
                </div>
              </Card>

              {/* Key Features */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Key Features
                </h3>
                <div className="space-y-3">
                  <Feature
                    icon={<Search className="h-5 w-5 text-blue-600" />}
                    title="Service Catalog"
                    description="Assign SAP Service IDs to specific customers"
                  />
                  <Feature
                    icon={<BarChart3 className="h-5 w-5 text-green-600" />}
                    title="Service Usage"
                    description="View and manage services assigned to customer"
                  />
                  <Feature
                    icon={<TrendingUp className="h-5 w-5 text-orange-600" />}
                    title="Change Impact Analysis"
                    description="Analyze changes and deprecations for customer specific services"
                  />
                  <Feature
                    icon={<Sparkles className="h-5 w-5 text-green-600" />}
                    title="What's New"
                    description="Discover new features and capabilities, changes, and anouncements made by SAP"
                  />
                  <Feature
                    icon={<FileText className="h-5 w-5 text-purple-600" />}
                    title="Reporting"
                    description="Generate comprehensive service reports"
                  />
                </div>
              </Card>
            </div>
          </div>
        ) : (
          /* No Customer Selected - Show Customer Selection Interface */
          <Card className="p-8 text-center">
            {accessibleCustomers?.length === 0 ? (
              /* No Customers Available */
              <>
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">No Customers Yet</h3>
                <p className="text-gray-600 mb-4">
                  {isRegularUser
                    ? "Get started by creating your first customer to begin managing SAP Digital Manufacturing services."
                    : "You don't have access to any customers yet. Contact an administrator to get assigned to customers, or create a new one."}
                </p>
                {canCreateCustomer && (
                  <Button
                    onClick={() => setCreateDialogOpen(true)}
                    design="Emphasized"
                  >
                    Create Your First Customer
                  </Button>
                )}
              </>
            ) : (
              /* Has Customers But None Selected */
              <>
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
                <h3 className="text-lg font-semibold mb-2">
                  No Customer Selected
                </h3>
                <p className="text-gray-600 mb-4">
                  Please select a customer to view their dashboard and manage
                  services.
                </p>
                <Button onClick={() => navigate(ROUTES.CUSTOMER_SELECTION)}>
                  Select Customer
                </Button>
              </>
            )}
          </Card>
        )}
      </PageContainer>

      {/* Create Customer Dialog */}
      {canCreateCustomer && (
        <CustomerCreateDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCustomerCreated}
        />
      )}

      {/* Edit Customer Dialog */}
      {canManageAccess && selectedCustomer && (
        <CustomerEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          customer={selectedCustomer}
          onSuccess={handleCustomerUpdated}
        />
      )}

      {/* Manage Access Modal */}
      {canManageAccess && selectedCustomer && (
        <ManageAccessModal
          open={manageAccessOpen}
          onClose={() => setManageAccessOpen(false)}
          customer={selectedCustomer}
          onUpdate={() => {
            // Trigger refresh of customer info card
            setRefreshKey((prev) => prev + 1);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onAfterClose={() => setDeleteDialogOpen(false)}
        headerText="Delete Customer"
        state="Warning"
        footer={
          <>
            <Button
              design="Emphasized"
              onClick={handleDeleteCustomer}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <Button
              design="Transparent"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete{" "}
            <strong className="text-gray-900">{selectedCustomer?.name}</strong>?
          </p>
          <p className="text-sm text-gray-600">
            This action cannot be undone. All associated data including
            services, consultants, and change impacts will be removed.
          </p>
        </div>
      </Dialog>

      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
      />
    </>
  );
};

// Helper Components

const Step = ({ number, text }) => (
  <div className="flex items-start gap-3">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
      {number}
    </div>
    <p className="pt-0.5">{text}</p>
  </div>
);

const Feature = ({ icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="flex-shrink-0 mt-0.5">{icon}</div>
    <div>
      <h4 className="font-medium text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  </div>
);

export default Dashboard;
